<template>
  <div ref="wrapperRef" class="comp-fh-ai-summary" :class="status">
    <div class="head">
      <div class="title">{{ $t('AI总结汇总') }}</div>
      <div class="btns">
        <jw-tooltip :content="$t('刷新')">
          <jw-button class="btn" size="mini" :disabled="status === 'loading'" @click="createSummaryTask">
            <template #icon>
              <qfei-icon-refresh-outlined />
            </template>
          </jw-button>
        </jw-tooltip>
        <span class="split-line" />
        <jw-tooltip :content="$t('关闭')">
          <jw-button @click="emits('close', $event)" class="btn" size="mini">
            <template #icon>
              <qfei-icon-close-one-outlined />
            </template>
          </jw-button>
        </jw-tooltip>
      </div>
    </div>
    <div class="content">
      <div v-if="status === 'loading'" class="loading-content">
        <qfei-icon-star-color-colored class="loading-icon" />
        <div class="loading-text">{{ $t('正在生成中') }}</div>
      </div>
      <div v-else-if="status === 'error'" class="error-content">
        <img :src="summaryError" alt="error" class="error-img" />
        <div class="error-text">{{ errorMsg }}</div>
      </div>
      <div v-else class="success-content">
        <!-- 使用iframe做样式隔离 -->
        <iframe ref="iframeEl" class="success-iframe" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watchEffect } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { QfeiIconRefreshOutlined, QfeiIconCloseOneOutlined, QfeiIconStarColorColored } from '@qfei-design/icons-vue';
import summaryError from '@/assets/images/ai/summary-error.svg';
import DOMPurify from 'dompurify';
import { aiService } from '@/api';
import { useI18n } from 'vue-i18n';
import { getToken } from '@/utils/auth';
import { tools } from '@/utils/tools';

const props = defineProps({
  data: {
    type: Object
  },
  entityId: {
    type: [String, Number]
  }
});

const emits = defineEmits(['close']);
const { t } = useI18n();
const wrapperRef = ref(null);
const status = ref('loading');
const errorMsg = ref('');
const iframeEl = ref(null);

// 点击外部关闭弹窗
onClickOutside(wrapperRef, (e) => {
  emits('close', e);
});

watchEffect(() => {
  if (iframeEl.value) {
    const style = iframeEl.value.contentWindow.document.createElement('style');
    // 重置 iframe 内的样式
    style.innerHTML = '* { margin: 0; padding: 0; } ::-webkit-scrollbar { width: 4px }';
    iframeEl.value.contentWindow.document.head.appendChild(style);
  }
});

/**
 * 设置 iframe 内容
 * @param html 要设置的 HTML 内容
 * @param scroll 是否滚动到最底部
 */
const setIframeContent = (html, scroll = true) => {
  const contentWindow = iframeEl.value?.contentWindow;
  if (html && contentWindow) {
    html = html
      .replace(/^```(?:html)?/, '') // 移除 markdown 标签
      .replace(/```$/, '') // 移除 markdown 标签
      .replace(/<\/?$/, ''); // 移除末尾的的尖括号
    // 对 HTML 进行清理，防止 XSS 攻击
    contentWindow.document.body.innerHTML = DOMPurify.sanitize(html);
    // 滚动到最底部
    if (scroll) {
      contentWindow.scrollTo(0, contentWindow.document.body.scrollHeight);
    }
  }
};

/**
 * 创建总结任务
 */
let cancelToken = null;
const createSummaryTask = async () => {
  status.value = 'loading';
  try {
    // 取消之前的请求
    cancelToken?.abort();
    // 创建新的取消令牌
    cancelToken = new AbortController();
    const res = await fetch('/api/v1/ai/summary/gen-stream', {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`
      },
      body: JSON.stringify({
        entity: props.entityId,
        businessId: props.data.id
      }),
      signal: cancelToken?.signal
    });
    // 这里状态应该是200 响应体应该是stream类型 否则抛出错误
    if (res.status !== 200 || res.headers.get('Content-Type') !== 'text/event-stream') {
      throw new Error('请求失败');
    }
    status.value = 'success';
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let content = '';
    let preStr = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const decodedChunk = preStr + decoder.decode(value);
      const lines = decodedChunk.split('\n');
      lines.forEach((line) => {
        if (line === 'data:"DONE"') return;
        if (line.startsWith('data:')) {
          const data = tools.safeParseJson(line.slice(5));
          if (data.success) {
            content += data.result?.content || '';
          } else {
            preStr = line;
          }
        }
      });
      setIframeContent(content);
    }
  } catch (error) {
    // 取消请求时不处理错误
    if (error.name === 'AbortError') return;
    console.error('请求失败:', error);
    fetchData();
  }
};

/**
 * 轮询获取总结内容
 */
let timer = null;
const fetchData = async () => {
  // 取消之前的定时器
  clearTimeout(timer);
  try {
    const res = await aiService.getSummaryContent({
      entity: props.entityId,
      businessId: props.data.id
    });
    if (!res.error) {
      switch (res.result.status) {
        // 初始化状态
        case 0:
          createSummaryTask();
          break;
        // 处理中状态
        case 1:
          status.value = 'loading';
          // 5秒后重试
          timer = setTimeout(() => {
            fetchData();
          }, 5e3);
          break;
        // 成功状态
        case 2:
          status.value = 'success';
          await nextTick();
          setIframeContent(res.result.content, false);
          break;
        // 失败状态
        case 3:
        default:
          status.value = 'error';
          errorMsg.value = res.result.errorMessage || t('生成失败');
          break;
      }
    } else {
      status.value = 'error';
      errorMsg.value = res.error.msg || t('生成失败');
    }
  } catch (error) {
    status.value = 'error';
    errorMsg.value = t('生成失败');
  }
};

onMounted(() => {
  fetchData();
});

onBeforeUnmount(() => {
  // 组件卸载时取消请求
  cancelToken?.abort();
  // 组件卸载时取消定时器
  clearTimeout(timer);
});
</script>
<style scoped lang="less">
@import './fh-ai-summary.less';
</style>
